// src/api/modificadores.js
import { apiFetch } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';
import { formatearMonto } from './productos.js';

const getProp = (obj, propName) => {
  if (!obj) return undefined;
  const keys = Object.keys(obj);
  const np = propName.toLowerCase().replace(/_/g, '');
  const found = keys.find(k => k.toLowerCase().replace(/_/g, '') === np);
  return found ? obj[found] : undefined;
};

const unwrapSingle = (res) => {
  if (!res) return null;
  if (res.data && !Array.isArray(res.data) && typeof res.data === 'object') return res.data;
  return res;
};

const unwrapList = (res) => {
  if (!res) return [];
  if (res.data && Array.isArray(res.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
};

// ================= MODIFICADOR =================
export const convertirModificadorDesdeAPI = (raw) => {
  const s = unwrapSingle(raw);
  if (!s) return null;
  const id = s.idModificador ?? s.IdModificador ?? getProp(s, 'idModificador') ?? s.id;
  return {
    idModificador: id, id,
    nombre: toTitleCase(s.nombre || s.Nombre || ''),
    tipo: (s.tipo || s.Tipo || 'opcion').toLowerCase(),
    es_obligatorio: s.es_obligatorio ?? s.esObligatorio ?? s.EsObligatorio ?? false,
    seleccion_multiple: s.seleccion_multiple ?? s.seleccionMultiple ?? s.SeleccionMultiple ?? false,
    max_selecciones: s.max_selecciones ?? s.maxSelecciones ?? s.MaxSelecciones ?? 1,
    estado: (s.estado || s.Estado || 'activo').toLowerCase(),
    fecha_creacion: s.fecha_creacion || s.fechaCreacion || s.FechaCreacion || null,
    TotalProductos: s.TotalProductos ?? s.totalProductos ?? 0,
    TotalOpciones: s.TotalOpciones ?? s.totalOpciones ?? 0,
    ...s, idModificador: id,
  };
};

export const convertirModificadorParaAPI = (m) => ({
  nombre: toTitleCase((m.nombre || '').trim()),
  tipo: m.tipo || 'opcion',
  es_obligatorio: !!m.es_obligatorio,
  seleccion_multiple: !!m.seleccion_multiple,
  max_selecciones: Number(m.max_selecciones) || 1,
  estado: m.estado || 'activo',
});

// ================= OPCION =================
export const convertirOpcionDesdeAPI = (raw) => {
  const s = unwrapSingle(raw);
  if (!s) return null;
  const id = s.idOpcionModificador ?? s.IdOpcionModificador ?? getProp(s, 'idOpcionModificador') ?? s.id;
  return {
    idOpcionModificador: id, id,
    idModificador: s.idModificador ?? s.IdModificador ?? null,
    nombreModificador: toTitleCase(s.nombreModificador || s.NombreModificador || ''),
    nombre: toTitleCase(s.nombre || s.Nombre || ''),
    precio_extra_usd: Number(s.precio_extra_usd ?? s.precioExtraUsd ?? s.PrecioExtraUsd ?? 0),
    precio_extra_bs: Number(s.precio_extra_bs ?? s.precioExtraBs ?? s.PrecioExtraBs ?? 0),
    es_default: s.es_default ?? s.esDefault ?? s.EsDefault ?? false,
    orden: s.orden ?? s.Orden ?? 0,
    estado: (s.estado || s.Estado || 'activo').toLowerCase(),
    ...s, idOpcionModificador: id,
  };
};

export const convertirOpcionParaAPI = (o) => ({
  idModificador: Number(o.idModificador) || 0,
  nombre: toTitleCase((o.nombre || '').trim()),
  precio_extra_usd: Number(o.precio_extra_usd) || 0,
  precio_extra_bs: Number(o.precio_extra_bs) || 0,
  es_default: !!o.es_default,
  orden: Number(o.orden) || 0,
  estado: o.estado || 'activo',
});

// ================= MODIFICADOR-PRODUCTO =================
export const convertirModificadorProductoDesdeAPI = (raw) => {
  const s = unwrapSingle(raw);
  if (!s) return null;
  const id = s.idModificadorProducto ?? s.IdModificadorProducto ?? getProp(s, 'idModificadorProducto') ?? s.id;
  return {
    idModificadorProducto: id,
    idMenuProducto: s.idMenuProducto ?? s.IdMenuProducto ?? null,
    nombreMenuProducto: toTitleCase(s.nombreMenuProducto || s.NombreMenuProducto || ''),
    idModificador: s.idModificador ?? s.IdModificador ?? null,
    nombreModificador: toTitleCase(s.nombreModificador || s.NombreModificador || ''),
    ...s, idModificadorProducto: id,
  };
};

// ================= HELPERS UI =================
export const convertirEstadoATexto = (e) =>
  e ? (e.charAt(0).toUpperCase() + e.slice(1)) : 'Sin estado';

export const formatearFecha = (iso, completo = false) => {
  if (!iso) return 'N/A';
  try {
    const d = new Date(iso);
    const opt = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) { opt.hour = '2-digit'; opt.minute = '2-digit'; }
    return d.toLocaleDateString('es-ES', opt);
  } catch { return iso; }
};

export { formatearMonto };

// ================= API CALLS =================

// MODIFICADOR
export const obtenerModificadores = async () => {
  try {
    const r = await apiFetch('/Modificador');
    const list = unwrapList(r).map(convertirModificadorDesdeAPI).filter(Boolean);
    return { success: true, data: list, count: list.length };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const obtenerModificadorPorId = async (id) => {
  try {
    const r = await apiFetch(`/Modificador/${id}`);
    return { success: true, data: convertirModificadorDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const obtenerModificadorConRelaciones = async (id) => {
  try {
    const r = await apiFetch(`/Modificador/${id}/conRelaciones`);
    return { success: true, data: convertirModificadorDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const buscarModificadores = async (criterios = {}) => {
  try {
    const r = await apiFetch('/Modificador/search', {
      method: 'POST',
      body: JSON.stringify({
        nombre: criterios.nombre || null,
        tipo: criterios.tipo || null,
        estado: criterios.estado || null,
      }),
    });
    return { success: true, data: unwrapList(r).map(convertirModificadorDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const crearModificador = async (m) => {
  try {
    if (!m.nombre || !m.nombre.trim()) throw new Error('El nombre es requerido');
    const r = await apiFetch('/Modificador', {
      method: 'POST',
      body: JSON.stringify(convertirModificadorParaAPI(m)),
    });
    return { success: true, data: convertirModificadorDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const actualizarModificador = async (id, m) => {
  try {
    if (!m.nombre || !m.nombre.trim()) throw new Error('El nombre es requerido');
    const r = await apiFetch(`/Modificador/${id}`, {
      method: 'PUT',
      body: JSON.stringify(convertirModificadorParaAPI(m)),
    });
    return { success: true, data: convertirModificadorDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const eliminarModificador = async (id) => {
  try {
    const r = await apiFetch(`/Modificador/${id}`, { method: 'DELETE' });
    return { success: true, data: r };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

// OPCIONES
export const obtenerOpcionesPorModificador = async (idModificador) => {
  try {
    const r = await apiFetch(`/Modificador/modificador/${idModificador}/opciones`);
    return { success: true, data: unwrapList(r).map(convertirOpcionDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const obtenerOpcionPorId = async (id) => {
  try {
    const r = await apiFetch(`/Modificador/opciones/${id}`);
    return { success: true, data: convertirOpcionDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const crearOpcion = async (o) => {
  try {
    if (!o.nombre || !o.nombre.trim()) throw new Error('El nombre es requerido');
    const r = await apiFetch('/Modificador/opciones', {
      method: 'POST',
      body: JSON.stringify(convertirOpcionParaAPI(o)),
    });
    return { success: true, data: convertirOpcionDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const actualizarOpcion = async (id, o) => {
  try {
    if (!o.nombre || !o.nombre.trim()) throw new Error('El nombre es requerido');
    const r = await apiFetch(`/Modificador/opciones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(convertirOpcionParaAPI(o)),
    });
    return { success: true, data: convertirOpcionDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const eliminarOpcion = async (id) => {
  try {
    const r = await apiFetch(`/Modificador/opciones/${id}`, { method: 'DELETE' });
    return { success: true, data: r };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

// ================= MODIFICADOR-PRODUCTO =================

// Productos de un modificador (endpoint inverso)
export const obtenerProductosPorModificador = async (idModificador) => {
  try {
    if (!idModificador) throw new Error('ID de modificador requerido');
    const r = await apiFetch(`/Modificador/modificador/${idModificador}/productos`);
    return {
      success: true,
      data: unwrapList(r).map(convertirModificadorProductoDesdeAPI).filter(Boolean),
    };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

// Modificadores de un producto
export const obtenerModificadoresDeProducto = async (idMenuProducto) => {
  try {
    const r = await apiFetch(`/Modificador/producto/${idMenuProducto}/modificadores`);
    return { success: true, data: unwrapList(r).map(convertirModificadorProductoDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const asignarModificadorAProducto = async (idMenuProducto, idModificador) => {
  try {
    const r = await apiFetch('/Modificador/producto/asignar', {
      method: 'POST',
      body: JSON.stringify({ idMenuProducto, idModificador }),
    });
    return { success: true, data: convertirModificadorProductoDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const quitarModificadorDeProducto = async (idModificadorProducto) => {
  try {
    const r = await apiFetch(`/Modificador/producto/${idModificadorProducto}`, { method: 'DELETE' });
    return { success: true, data: r };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

// ================= EXPORT DEFAULT =================
export default {
  // Modificador
  obtenerModificadores, obtenerModificadorPorId, obtenerModificadorConRelaciones,
  buscarModificadores, crearModificador, actualizarModificador, eliminarModificador,
  // Opciones
  obtenerOpcionesPorModificador, obtenerOpcionPorId,
  crearOpcion, actualizarOpcion, eliminarOpcion,
  // Modificador-Producto
  obtenerModificadoresDeProducto, obtenerProductosPorModificador,
  asignarModificadorAProducto, quitarModificadorDeProducto,
  // Helpers
  formatearMonto, formatearFecha, convertirEstadoATexto,
};